import {
  Badge,
  Box,
  Group,
  Paper,
  Progress,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title, Skeleton } from '@mantine/core';
import type { ReactNode } from 'react';
import { estateTint, providerTint, toneTint, tokens as T, type Tone } from '../theme/tokens';
import classes from './ui.module.css';

/* The console's shared primitives.
 *
 * Each one exists because the design repeats it, not because a wrapper felt
 * tidy: a pill appears ~200 times, a table nine times, a meter row four. The
 * rule for adding one is the same as the rule for adding anything here — it
 * must already be repeating. */

// ------------------------------------------------------------------ pills

const PILL_SIZES = {
  sm: { padY: 3, padX: 7, radius: 5 },
  md: { padY: 4, padX: 8, radius: 5 },
  lg: { padY: 5, padX: 9, radius: 6 },
  xl: { padY: 6, padX: 10, radius: 6 },
} as const;

export type PillSize = keyof typeof PILL_SIZES;

function pillVars(bg: string, fg: string, size: PillSize) {
  const s = PILL_SIZES[size];
  return {
    root: {
      '--badge-bg': bg,
      '--badge-color': fg,
      '--badge-bd': 'none',
      // Content height, not Mantine's fixed 20px: the design's pills are four
      // different heights and all of them are set by their own padding.
      '--badge-height': 'auto',
      '--badge-radius': `${s.radius}px`,
      '--badge-padding-x': `${s.padX}px`,
      '--badge-fz': '10px',
      '--badge-lh': '1.25',
    },
  };
}

export function Pill({
  tone = 'neutral',
  size = 'md',
  children,
}: {
  tone?: Tone;
  size?: PillSize;
  children: ReactNode;
}) {
  const [bg, fg] = toneTint[tone];
  return (
    <Badge
      variant="filled"
      vars={() => pillVars(bg, fg, size)}
      styles={{
        root: { paddingBlock: PILL_SIZES[size].padY },
        label: { fontWeight: 800, letterSpacing: '0.06em', overflow: 'visible' },
      }}
    >
      {children}
    </Badge>
  );
}

export function ProviderPill({ provider, size = 'lg' }: { provider: string; size?: PillSize }) {
  const [bg, fg] = providerTint(provider);
  return (
    <Badge
      variant="filled"
      vars={() => pillVars(bg, fg, size)}
      styles={{
        root: { paddingBlock: PILL_SIZES[size].padY },
        label: { fontWeight: 800, letterSpacing: '0.06em', overflow: 'visible' },
      }}
    >
      {provider.toUpperCase()}
    </Badge>
  );
}

export function EstatePill({ estate, size = 'sm' }: { estate: string; size?: PillSize }) {
  const [bg, fg] = estateTint(estate);
  return (
    <Badge
      variant="filled"
      vars={() => pillVars(bg, fg, size)}
      styles={{
        root: { paddingBlock: PILL_SIZES[size].padY },
        label: { fontWeight: 800, letterSpacing: '0.06em', overflow: 'visible' },
      }}
    >
      {estate.toUpperCase()}
    </Badge>
  );
}

// ------------------------------------------------------------- typography

/** The 10px, 700-weight, wide-tracked caption above every card section. */
export function ColLabel({ children, mb }: { children: ReactNode; mb?: number }) {
  return (
    <Text
      component="div"
      fz={10}
      fw={700}
      c={T.ink5}
      mb={mb}
      style={{ letterSpacing: '0.09em', textTransform: 'uppercase' }}
    >
      {children}
    </Text>
  );
}

export function Mono({
  children,
  c = T.ink3,
  fz = 12,
  truncate,
}: {
  children: ReactNode;
  c?: string;
  fz?: number;
  truncate?: boolean;
}) {
  return (
    <Text
      component="span"
      ff="monospace"
      fz={fz}
      c={c}
      style={
        truncate
          ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }
          : undefined
      }
    >
      {children}
    </Text>
  );
}

export function PageHeader({
  title,
  lede,
  actions,
  aside,
}: {
  title: ReactNode;
  lede?: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <Group justify="space-between" align="flex-start" gap={24} wrap="wrap">
      <Box maw={760}>
        <Title order={1} mb={lede ? 12 : 0}>
          {title}
        </Title>
        {lede ? (
          <Text fz={15} lh={1.55} c={T.ink3} style={{ textWrap: 'pretty' }}>
            {lede}
          </Text>
        ) : null}
      </Box>
      {aside ?? null}
      {actions ? (
        <Group gap={10} wrap="nowrap" style={{ flex: '0 0 auto' }}>
          {actions}
        </Group>
      ) : null}
    </Group>
  );
}

// ------------------------------------------------------------------ cards

export function SectionCard({
  title,
  subtitle,
  right,
  children,
  p = '18px 20px',
  ...rest
}: {
  title?: ReactNode;
  subtitle?: ReactNode;
  right?: ReactNode;
  children: ReactNode;
  p?: string | number;
  mb?: number;
  mt?: number;
}) {
  return (
    <Paper radius="xl" p={p} {...rest}>
      {title || right ? (
        <Group justify="space-between" align="baseline" gap={16} wrap="wrap">
          <Box>
            <Text fz={16} fw={800}>
              {title}
            </Text>
            {subtitle ? (
              <Text fz={12} c={T.ink4} mt={3}>
                {subtitle}
              </Text>
            ) : null}
          </Box>
          {right ?? null}
        </Group>
      ) : null}
      {children}
    </Paper>
  );
}

export function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  color?: string;
}) {
  return (
    <Paper radius="lg" p="16px 18px">
      <ColLabel>{label}</ColLabel>
      {/* A div, not a p: `value` is any node, and while a figure is pending it
          is a Skeleton, which is a div — invalid inside a p, and React says so
          on every card. Mantine zeroes Text's margins, so the box is the same. */}
      <Text component="div" fz={26} fw={800} mt={6} c={color ?? T.ink} lh={1.15}>
        {value}
      </Text>
      {sub ? (
        <Text fz={12} c={T.ink4} mt={3}>
          {sub}
        </Text>
      ) : null}
    </Paper>
  );
}

/** `repeat(auto-fit, minmax(N, 1fr))`.
 *
 * Neither SimpleGrid (breakpoint-driven column counts) nor Grid (a 12-column
 * system) expresses a container-width-driven auto-fit, and the difference is
 * real: the drawer's grids must reflow on the DRAWER's width, not the
 * viewport's. So this one stays a plain Box. */
export function AutoGrid({
  min,
  gap = 16,
  children,
  mt,
  mb,
  align,
}: {
  min: number;
  gap?: number;
  children: ReactNode;
  mt?: number;
  mb?: number;
  align?: 'start' | 'stretch';
}) {
  return (
    <Box
      mt={mt}
      mb={mb}
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap,
        alignItems: align === 'start' ? 'start' : undefined,
      }}
    >
      {children}
    </Box>
  );
}

/** A key/value row inside a bordered stack — the drawer's detail sections and
 *  the estate policy cards. Not a Table: there is no header and no column. */
export function KV({
  k,
  v,
  fg,
  mono,
}: {
  k: ReactNode;
  v: ReactNode;
  fg?: string;
  mono?: boolean;
}) {
  return (
    <Group justify="space-between" align="baseline" gap={14} wrap="nowrap">
      <Text fz={12} c={T.ink4} style={{ whiteSpace: 'nowrap' }}>
        {k}
      </Text>
      <Text
        fz={12}
        fw={600}
        c={fg ?? T.ink}
        ff={mono ? 'monospace' : undefined}
        ta="right"
        style={{ wordBreak: 'break-all' }}
      >
        {v}
      </Text>
    </Group>
  );
}

export function KVSection({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <Box>
      <ColLabel mb={9}>{title}</ColLabel>
      <Box
        style={{
          border: `1px solid ${T.cardBorder}`,
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export function KVRow({ children }: { children: ReactNode }) {
  return (
    <Box style={{ padding: '9px 13px', borderBottom: `1px solid ${T.rowLine}` }}>{children}</Box>
  );
}

// ------------------------------------------------------------- chip rail

/** The pill filter rail. Eleven of these in the design, and every one is
 *  single-select radio semantics with an animated indicator — which is exactly
 *  SegmentedControl, and exactly what a Button group would make us hand-roll.
 *
 *  It must never wrap: the indicator is positioned from a measured rect, so a
 *  flex-wrapped rail desynchronises it permanently. The row AROUND a rail
 *  wraps instead. */
export function ChipRail<V extends string>({
  value,
  onChange,
  data,
  size = 'md',
  fullWidth,
}: {
  value: V;
  onChange: (v: V) => void;
  data: { value: V; label: ReactNode }[];
  size?: 'sm' | 'md';
  fullWidth?: boolean;
}) {
  const pad = size === 'sm' ? '6px 12px' : '7px 14px';
  return (
    <SegmentedControl
      value={value}
      onChange={(v) => onChange(v as V)}
      data={data as { value: string; label: ReactNode }[]}
      radius={999}
      withItemsBorders={false}
      fullWidth={fullWidth}
      transitionDuration={140}
      styles={{
        root: {
          background: T.rail,
          border: `1px solid ${T.border}`,
          padding: 3,
          flex: '0 0 auto',
          '--sc-color': T.card,
          '--sc-shadow': '0 1px 2px rgba(46,42,38,0.12)',
          '--sc-label-color': T.ink,
        },
        label: { fontSize: 12, fontWeight: 700, padding: pad, whiteSpace: 'nowrap' },
      }}
    />
  );
}

// ------------------------------------------------------------ meter rows

/** A labelled bar. The design has four of these lists and none of them is a
 *  chart: each puts a rich, baseline-aligned header row above a 5–7px track,
 *  a layout Mantine's BarsList (name inside the bar) cannot produce. */
export function MeterRow({
  left,
  right,
  value,
  color,
  after,
  height = 7,
  onClick,
}: {
  left: ReactNode;
  right?: ReactNode;
  value: number;
  color: string;
  after?: ReactNode;
  height?: number;
  onClick?: () => void;
}) {
  return (
    <Box onClick={onClick} style={onClick ? { cursor: 'pointer' } : undefined}>
      <Group justify="space-between" align="baseline" gap={10} wrap="nowrap">
        {left}
        {right ?? null}
      </Group>
      <Group gap={10} mt={6} wrap="nowrap" align="center">
        <Progress
          value={Math.max(0, Math.min(100, value))}
          color={color}
          size={height}
          style={{ flex: 1 }}
          transitionDuration={0}
        />
        {after ?? null}
      </Group>
    </Box>
  );
}

// ----------------------------------------------------------------- table

export interface Col {
  key: string;
  label?: ReactNode;
  /** Fixed px width. Exactly one column should omit it — under table-layout:
   *  fixed an auto column absorbs the remainder, which is the design's `1fr`. */
  w?: number;
  align?: 'left' | 'right';
}

/** The console's table.
 *
 * The design draws these as a CSS grid with fixed pixel columns, a min-width
 * and overflow-x. Every part of that has a first-class equivalent here —
 * ScrollContainer for the overflow, a colgroup under table-layout:fixed for the
 * columns — and using them keeps the table a table, which matters for the row
 * semantics a nine-column operator table depends on. */
export function ConsoleTable({
  cols,
  minWidth,
  children,
  hover,
}: {
  cols: Col[];
  minWidth: number;
  children: ReactNode;
  hover?: boolean;
}) {
  return (
    <Paper radius="xl" p={0} style={{ overflow: 'hidden' }}>
      <Table.ScrollContainer minWidth={minWidth} type="native" className="rt-scroll">
        <Table
          layout="fixed"
          classNames={{ table: classes.table, thead: classes.tableHead }}
          highlightOnHover={hover}
          highlightOnHoverColor="var(--rt-row-hover)"
          borderColor="var(--rt-row-line)"
          horizontalSpacing={6}
          verticalSpacing={11}
          tabularNums
          withRowBorders
        >
          <colgroup>
            {cols.map((c) => (
              <col key={c.key} style={c.w ? { width: c.w } : undefined} />
            ))}
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              {cols.map((c) => (
                <Table.Th key={c.key} ta={c.align === 'right' ? 'right' : undefined}>
                  {c.label ?? ''}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>{children}</Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Paper>
  );
}

export function Row({
  onClick,
  selected,
  children,
}: {
  onClick?: () => void;
  selected?: boolean;
  children: ReactNode;
}) {
  const cls = [onClick ? classes.rowClickable : '', selected ? classes.rowSelected : '']
    .filter(Boolean)
    .join(' ');
  return (
    <Table.Tr onClick={onClick} className={cls || undefined}>
      {children}
    </Table.Tr>
  );
}

/** The in-table message row. It takes the phase, not a boolean, because
 *  "never connected" and "ran and matched nothing" are different facts and an
 *  operator acts differently on each. */
export function TableMessage({
  span,
  title,
  detail,
}: {
  span: number;
  title: string;
  detail?: string;
}) {
  return (
    <Table.Tr>
      <Table.Td colSpan={span} style={{ padding: '34px 20px', textAlign: 'center' }}>
        <Text fz={14} fw={700} c={T.ink2}>
          {title}
        </Text>
        {detail ? (
          <Text fz={13} c={T.ink4} mt={5}>
            {detail}
          </Text>
        ) : null}
      </Table.Td>
    </Table.Tr>
  );
}

// ----------------------------------------------------------- empty state

export function NotConnected({
  what,
  detail,
}: {
  what: string;
  detail?: string;
}) {
  return (
    <Paper radius="xl" p="34px 20px">
      <Stack gap={5} align="center">
        <Text fz={14} fw={700} c={T.ink2}>
          Not connected — {what}
        </Text>
        <Text fz={13} c={T.ink4} ta="center" maw={620}>
          {detail ??
            'The control plane has no read route for this yet, so this console cannot show it. This is not the same as there being nothing to show.'}
        </Text>
      </Stack>
    </Paper>
  );
}

/* Loading placeholders.
 *
 * The rule these exist to serve: a skeleton stands in for data that has NEVER
 * arrived, never for data already on screen. A refresh — a range change, a
 * re-scope — keeps the resolved series mounted, because a chart that empties
 * itself for two seconds every time an operator changes the range is the flash
 * this console does not do (spec/console.md §Update discipline).
 *
 * They also exist because the honest alternative was a lie. Before this, a
 * panel with no data yet rendered "not connected", which is a claim about the
 * BACKEND — so a warehouse query that simply took a few seconds looked like a
 * missing route.
 */

/** StatSkeleton stands in for a KPI value.
 *
 * Sized to the digits it replaces rather than to the card, so the row does not
 * change height when the numbers land. */
export function StatSkeleton() {
  return <Skeleton height={26} width={86} radius="sm" mt={6} />;
}

/** ChartSkeleton stands in for a series.
 *
 * The exact height of the chart it replaces: a placeholder that is shorter
 * than its chart makes the whole page jump when the data arrives, which is the
 * same jolt the skeleton is meant to prevent.
 */
export function ChartSkeleton({ height }: { height: number }) {
  return <Skeleton height={height} radius="md" />;
}

/** RowsSkeleton stands in for a list or table body. */
export function RowsSkeleton({
  rows = 3,
  height = 16,
  gap = 14,
}: {
  rows?: number;
  height?: number;
  gap?: number;
}) {
  return (
    <Stack gap={gap} p="4px 0">
      {Array.from({ length: rows }, (_, i) => (
        // Widths taper so it reads as content rather than as a loading bar.
        <Skeleton key={i} height={height} radius="sm" width={`${100 - i * 12}%`} />
      ))}
    </Stack>
  );
}

export { classes as uiClasses };
